import {
  Activity,
  ClipboardCheck,
  FileClock,
  Gauge,
  Navigation,
  RadioTower,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Truck,
} from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { TenantSwitcher } from "@/components/tenant/TenantSwitcher";
import { CommandPalette } from "./CommandPalette";
import type { CommandLoadInput } from "@/core/console/command-palette";

const nav = [
  { href: "#command", label: "Command", icon: Activity },
  { href: "/loads", label: "Loads", icon: Truck },
  { href: "/loads", label: "Track", icon: Navigation },
  { href: "#exceptions", label: "Exceptions", icon: RadioTower },
  { href: "#approvals", label: "Approvals", icon: ClipboardCheck },
  { href: "#timeline", label: "Timeline", icon: FileClock },
  { href: "#policies", label: "Policies", icon: ShieldCheck },
  { href: "/admin/agent", label: "Agent Console", icon: Sparkles },
  { href: "/admin/agent-ops", label: "Agent Ops", icon: ShieldAlert },
];

export function AppShell({
  children,
  commandLoads = [],
  title = "Managed Freight Agent",
  description = "A read-and-draft agent layer over the broker TMS: evidence, exceptions, approvals, and operator controls.",
  badges,
}: {
  children: ReactNode;
  commandLoads?: CommandLoadInput[];
  title?: string;
  description?: string;
  badges?: ReactNode;
}) {
  return (
    <div className="min-h-screen bg-background">
      <aside className="fixed inset-y-0 left-0 hidden w-64 border-r border-border bg-card px-4 py-5 lg:block">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Gauge size={18} />
          </div>
          <div>
            <div className="text-sm font-semibold">Haulbase</div>
            <div className="text-xs text-muted-foreground">TMA Console</div>
          </div>
        </div>
        <nav className="space-y-1">
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <a
                key={item.href}
                href={item.href}
                className="flex h-9 items-center gap-3 rounded-md px-3 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Icon size={16} />
                {item.label}
              </a>
            );
          })}
        </nav>
      </aside>
      <main className="lg:pl-64">
        <header className="sticky top-0 z-10 border-b border-border bg-background/92 px-5 py-4 backdrop-blur">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-xl font-semibold tracking-tight">
                {title}
              </h1>
              <p className="text-sm text-muted-foreground">
                {description}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <TenantSwitcher />
              <CommandPalette loads={commandLoads} />
              {badges ?? (
                <>
                  <Badge variant="info">Tenant scoped</Badge>
                  <Badge variant="info">read_and_draft</Badge>
                  <Badge variant="warn">No unsafe external action sent</Badge>
                </>
              )}
            </div>
          </div>
        </header>
        <div className="mx-auto max-w-7xl px-5 py-6">{children}</div>
      </main>
    </div>
  );
}
